import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Upload, X, CheckCircle, Film, RefreshCw } from "lucide-react";
import { supabase } from "@/services/supabase";

interface DanceLabApplicationFormProps {
  eventSlug: string;
  eventTitle: string;
}

const DanceLabApplicationForm = ({ eventSlug, eventTitle }: DanceLabApplicationFormProps) => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    city: "",
    country: "",
    danceSchool: "",
    instagram: "",
    facebook: "",
    otherSocial: "",
    background: "",
    videoLink: "",
  });

  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadedFileUrl, setUploadedFileUrl] = useState<string | null>(null);
  const [uploadStatus, setUploadStatus] = useState<"idle" | "uploading" | "success" | "error">("idle");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const fileSizeMB = selectedFile.size / (1024 * 1024);
    if (selectedFile.size > 200 * 1024 * 1024) {
      setErrors((prev) => ({ ...prev, file: `File size (${fileSizeMB.toFixed(1)}MB) exceeds 200MB limit` }));
      return;
    }

    const allowedTypes = [
      "video/mp4", "video/webm", "video/quicktime", "video/x-msvideo", "video/avi",
      "video/x-m4v", "video/x-quicktime", "video/3gpp", "video/3gpp2", "video/mp2t", "video/mpeg", "",
    ];
    const allowedExtensions = [".mp4", ".webm", ".mov", ".avi", ".m4v", ".3gp", ".3g2", ".mts", ".mpeg", ".mpg"];
    const ext = selectedFile.name.toLowerCase().slice(selectedFile.name.lastIndexOf("."));

    if (!allowedTypes.includes(selectedFile.type) && !allowedExtensions.includes(ext)) {
      setErrors((prev) => ({
        ...prev,
        file: `Video format not supported. Accepted: MP4, MOV, WebM, AVI, M4V, 3GP. Your type: ${selectedFile.type || "unknown"}`,
      }));
      return;
    }

    setFile(selectedFile);
    setFilePreview(URL.createObjectURL(selectedFile));
    if (errors.file) {
      setErrors((prev) => { const n = { ...prev }; delete n.file; return n; });
    }
  };

  const removeFile = () => {
    setFile(null);
    if (filePreview) { URL.revokeObjectURL(filePreview); setFilePreview(null); }
    setUploadedFileUrl(null);
    setUploadStatus("idle");
    setUploadProgress(0);
  };

  const handleUploadNow = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!file || uploadStatus === "uploading") return;

    setUploadStatus("uploading");
    setUploadProgress(0);

    try {
      const { data: urlData, error: urlError } = await supabase.functions.invoke("upload-to-r2", {
        body: { fileName: file.name, contentType: file.type || "application/octet-stream" },
      });
      if (urlError || !urlData?.presignedUrl) throw new Error("Unable to prepare video upload");

      const maxRetries = 3;
      const baseDelay = 1000;

      const uploadWithRetry = async (attempt: number): Promise<void> => {
        return new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.addEventListener("progress", (ev) => {
            if (ev.lengthComputable) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
          });
          xhr.addEventListener("load", () => {
            if (xhr.status >= 200 && xhr.status < 300) { setUploadProgress(100); resolve(); }
            else if (xhr.status >= 500 && attempt < maxRetries) {
              setTimeout(() => { setUploadProgress(0); uploadWithRetry(attempt + 1).then(resolve).catch(reject); }, baseDelay * Math.pow(2, attempt));
            } else reject(new Error(`Upload failed (${xhr.status})`));
          });
          xhr.addEventListener("error", () => {
            if (attempt < maxRetries) {
              setTimeout(() => { setUploadProgress(0); uploadWithRetry(attempt + 1).then(resolve).catch(reject); }, baseDelay * Math.pow(2, attempt));
            } else reject(new Error("Upload failed after multiple attempts"));
          });
          xhr.addEventListener("abort", () => reject(new Error("Upload was cancelled")));
          xhr.open("PUT", urlData.presignedUrl);
          xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
          xhr.send(file);
        });
      };

      await uploadWithRetry(0);
      setUploadedFileUrl(urlData.publicUrl);
      setUploadStatus("success");
    } catch (error) {
      console.error("Pre-upload failed:", error);
      setUploadStatus("error");
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.fullName.trim()) newErrors.fullName = "Full name is required";
    if (!formData.email.trim()) newErrors.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = "Please enter a valid email address";
    if (!formData.city.trim()) newErrors.city = "City is required";
    if (!formData.country.trim()) newErrors.country = "Country is required";
    if (!formData.background.trim()) newErrors.background = "Please describe your dance background";
    else if (formData.background.length < 50) newErrors.background = "Please provide at least 50 characters about your dance background";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      let fileUrl = uploadedFileUrl;

      if (file && !uploadedFileUrl) {
        setIsUploading(true);
        setUploadProgress(0);
        try {
          const { data: urlData, error: urlError } = await supabase.functions.invoke("upload-to-r2", {
            body: { fileName: file.name, contentType: file.type || "application/octet-stream" },
          });
          if (urlError || !urlData?.presignedUrl) throw new Error("Unable to prepare video upload");

          const maxRetries = 3;
          const baseDelay = 1000;
          const uploadWithRetry = async (attempt: number): Promise<void> => {
            return new Promise<void>((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.upload.addEventListener("progress", (ev) => {
                if (ev.lengthComputable) setUploadProgress(Math.round((ev.loaded / ev.total) * 100));
              });
              xhr.addEventListener("load", () => {
                if (xhr.status >= 200 && xhr.status < 300) { setUploadProgress(100); resolve(); }
                else if (xhr.status >= 500 && attempt < maxRetries) {
                  setTimeout(() => { setUploadProgress(0); uploadWithRetry(attempt + 1).then(resolve).catch(reject); }, baseDelay * Math.pow(2, attempt));
                } else reject(new Error(`Video upload failed (${xhr.status})`));
              });
              xhr.addEventListener("error", () => {
                if (attempt < maxRetries) {
                  setTimeout(() => { setUploadProgress(0); uploadWithRetry(attempt + 1).then(resolve).catch(reject); }, baseDelay * Math.pow(2, attempt));
                } else reject(new Error("Video upload failed after multiple attempts"));
              });
              xhr.addEventListener("abort", () => reject(new Error("Video upload was cancelled")));
              xhr.open("PUT", urlData.presignedUrl);
              xhr.setRequestHeader("Content-Type", file.type || "application/octet-stream");
              xhr.send(file);
            });
          };
          await uploadWithRetry(0);
          fileUrl = urlData.publicUrl;
        } finally {
          setIsUploading(false);
        }
      }

      const { error } = await supabase.functions.invoke("submit-dance-lab-application", {
        body: {
          ...formData,
          program: eventTitle,
          eventSlug,
          videoFileUrl: fileUrl,
          fileName: file?.name || null,
        },
      });

      if (error) throw new Error(error.message || "Failed to submit application");

      alert("Thank you for your application to the Endless Dance Lab! Our team will review your submission and get back to you within 3–5 business days.");
      navigate("/dance-lab");
    } catch (error) {
      const msg = error instanceof Error ? error.message : "Failed to submit application. Please try again.";
      alert(`Application Error: ${msg}\n\nIf the problem persists, please email klauyros@gmail.com with your application details.`);
    } finally {
      setIsSubmitting(false);
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  const inputClass = "w-full px-4 py-3 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all";
  const labelStyle = { fontFamily: "'Grift', sans-serif", color: "#FFFFFF" } as const;

  return (
    <motion.form
      onSubmit={handleSubmit}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="space-y-6"
    >
      {/* Full Name */}
      <div>
        <label htmlFor="fullName" className="block mb-2 text-sm font-semibold" style={labelStyle}>Full Name *</label>
        <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} className={inputClass} style={labelStyle} placeholder="Enter your full name" />
        {errors.fullName && <p className="mt-1 text-sm text-red-400">{errors.fullName}</p>}
      </div>

      {/* Email */}
      <div>
        <label htmlFor="email" className="block mb-2 text-sm font-semibold" style={labelStyle}>Email Address *</label>
        <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={inputClass} style={labelStyle} placeholder="your.email@example.com" />
        {errors.email && <p className="mt-1 text-sm text-red-400">{errors.email}</p>}
      </div>

      {/* Phone Number */}
      <div>
        <label htmlFor="phone" className="block mb-2 text-sm font-semibold" style={labelStyle}>Phone Number (optional)</label>
        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={inputClass} style={labelStyle} placeholder="+1 234 567 8900" />
      </div>

      {/* City & Country */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="city" className="block mb-2 text-sm font-semibold" style={labelStyle}>City *</label>
          <input type="text" id="city" name="city" value={formData.city} onChange={handleChange} className={inputClass} style={labelStyle} placeholder="Enter your city" />
          {errors.city && <p className="mt-1 text-sm text-red-400">{errors.city}</p>}
        </div>
        <div>
          <label htmlFor="country" className="block mb-2 text-sm font-semibold" style={labelStyle}>Country *</label>
          <input type="text" id="country" name="country" value={formData.country} onChange={handleChange} className={inputClass} style={labelStyle} placeholder="Enter your country" />
          {errors.country && <p className="mt-1 text-sm text-red-400">{errors.country}</p>}
        </div>
      </div>

      {/* Dance School */}
      <div>
        <label htmlFor="danceSchool" className="block mb-2 text-sm font-semibold" style={labelStyle}>Name of Dance School (optional)</label>
        <input type="text" id="danceSchool" name="danceSchool" value={formData.danceSchool} onChange={handleChange} className={inputClass} style={labelStyle} placeholder="Enter dance school name" />
      </div>

      {/* Social Media */}
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="instagram" className="block mb-2 text-sm font-semibold" style={labelStyle}>Instagram</label>
          <input type="text" id="instagram" name="instagram" value={formData.instagram} onChange={handleChange} className={inputClass} style={labelStyle} placeholder="@username (optional)" />
        </div>
        <div>
          <label htmlFor="facebook" className="block mb-2 text-sm font-semibold" style={labelStyle}>Facebook</label>
          <input type="text" id="facebook" name="facebook" value={formData.facebook} onChange={handleChange} className={inputClass} style={labelStyle} placeholder="Facebook profile URL (optional)" />
        </div>
      </div>
      <div>
        <label htmlFor="otherSocial" className="block mb-2 text-sm font-semibold" style={labelStyle}>Other Social Media Links</label>
        <input type="text" id="otherSocial" name="otherSocial" value={formData.otherSocial} onChange={handleChange} className={inputClass} style={labelStyle} placeholder="TikTok, YouTube, etc. (optional)" />
      </div>

      {/* Dance Background */}
      <div>
        <label htmlFor="background" className="block mb-2 text-sm font-semibold" style={labelStyle}>Short Description of Your Dance Background *</label>
        <textarea
          id="background" name="background" value={formData.background} onChange={handleChange} rows={6}
          className={`${inputClass} resize-none`} style={labelStyle}
          placeholder="Tell us about your dance experience, training, and why you want to attend (minimum 50 characters)"
        />
        {errors.background && <p className="mt-1 text-sm text-red-400">{errors.background}</p>}
        <p className="mt-1 text-xs" style={{ color: "rgba(255, 255, 255, 0.5)" }}>
          {formData.background.length}/50 minimum characters
        </p>
      </div>

      {/* Video Link */}
      <div>
        <label htmlFor="videoLink" className="block mb-2 text-sm font-semibold" style={labelStyle}>Link to Video (Optional)</label>
        <input type="url" id="videoLink" name="videoLink" value={formData.videoLink} onChange={handleChange} className={inputClass} style={labelStyle} placeholder="https://youtube.com/watch?v=... or Vimeo link" />
      </div>

      {/* File Upload */}
      <div>
        <label className="block mb-2 text-sm font-semibold" style={labelStyle}>Upload Video File (Optional)</label>

        {!file && uploadStatus === "idle" && (
          <div className="flex items-center gap-4">
            <label
              htmlFor="danceLabFile"
              className="flex items-center gap-2 px-6 py-3 rounded-xl cursor-pointer transition-all border-2 border-dashed border-white/30 hover:border-orange-500/50"
              style={{ background: "rgba(255, 255, 255, 0.05)", fontFamily: "'Grift', sans-serif", color: "#FFFFFF" }}
            >
              <Upload size={20} />
              <span>Choose File</span>
            </label>
            <input type="file" id="danceLabFile" accept="video/*" onChange={handleFileChange} className="hidden" />
          </div>
        )}

        {file && uploadStatus === "idle" && (
          <div className="p-4 rounded-xl bg-white/5 border border-white/20">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 rounded-lg bg-white/10 shrink-0"><Film size={20} className="text-white/70" /></div>
                <div className="min-w-0">
                  <p className="text-white text-sm font-medium truncate">{file.name}</p>
                  <p className="text-white/50 text-xs">{(file.size / (1024 * 1024)).toFixed(1)}MB</p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={handleUploadNow} className="px-4 py-2 rounded-lg bg-orange-500 text-black text-sm font-semibold hover:bg-orange-400 transition-colors" style={{ fontFamily: "'Grift', sans-serif" }}>
                  Upload Now
                </button>
                <button type="button" onClick={removeFile} className="p-2 rounded-lg hover:bg-white/10 transition-colors"><X size={18} className="text-white/70" /></button>
              </div>
            </div>
            <p className="mt-2 text-xs text-white/40">Tip: Upload now to ensure your video is ready before submitting</p>
          </div>
        )}

        {uploadStatus === "uploading" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/30">
            <div className="flex items-center justify-between text-sm mb-3">
              <div className="flex items-center gap-2"><Loader2 size={16} className="animate-spin text-orange-500" /><span className="text-white">Uploading {file?.name}...</span></div>
              <span className="font-semibold text-orange-500">{uploadProgress}%</span>
            </div>
            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
              <motion.div className="h-full bg-gradient-to-r from-orange-500 to-orange-400 rounded-full" initial={{ width: 0 }} animate={{ width: `${uploadProgress}%` }} transition={{ duration: 0.3, ease: "easeOut" }} />
            </div>
          </motion.div>
        )}

        {uploadStatus === "success" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-xl bg-green-500/10 border border-green-500/30">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-2 rounded-lg bg-green-500/20 shrink-0"><CheckCircle size={20} className="text-green-500" /></div>
                <div className="min-w-0"><p className="text-green-500 font-medium text-sm">Video ready!</p><p className="text-white/70 text-xs truncate">{file?.name}</p></div>
              </div>
              <button type="button" onClick={removeFile} className="p-2 rounded-lg hover:bg-white/10 transition-colors shrink-0"><X size={18} className="text-white/50" /></button>
            </div>
          </motion.div>
        )}

        {uploadStatus === "error" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-4 rounded-xl bg-red-500/10 border border-red-500/30">
            <div className="flex items-center justify-between gap-4">
              <p className="text-red-400 text-sm">Upload failed. Please try again.</p>
              <div className="flex items-center gap-2 shrink-0">
                <button type="button" onClick={handleUploadNow} className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/20 text-red-400 text-sm hover:bg-red-500/30 transition-colors">
                  <RefreshCw size={14} /><span>Retry</span>
                </button>
                <button type="button" onClick={removeFile} className="p-2 rounded-lg hover:bg-white/10 transition-colors"><X size={18} className="text-white/50" /></button>
              </div>
            </div>
          </motion.div>
        )}

        {errors.file && <p className="mt-1 text-sm text-red-400">{errors.file}</p>}
        <p className="mt-2 text-xs" style={{ color: "rgba(255, 255, 255, 0.4)" }}>Max 200MB. Accepted formats: MP4, MOV, WebM, AVI, M4V</p>
      </div>

      {/* Submit */}
      <div className="pt-6">
        <button
          type="submit"
          disabled={isSubmitting || isUploading}
          className="w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{
            background: isSubmitting ? "rgba(230, 126, 34, 0.5)" : "linear-gradient(90deg, #E67E22 0%, #FF9E4F 100%)",
            color: "#FFFFFF",
            fontFamily: "'Grift', sans-serif",
            fontStyle: "italic",
            boxShadow: isSubmitting ? "none" : "0 4px 20px rgba(230, 126, 34, 0.4)",
          }}
        >
          {isSubmitting ? (
            <><Loader2 size={20} className="animate-spin" /><span>Submitting...</span></>
          ) : isUploading ? (
            <><Loader2 size={20} className="animate-spin" /><span>Uploading Video... {uploadProgress}%</span></>
          ) : (
            <span>Submit Application</span>
          )}
        </button>
      </div>
    </motion.form>
  );
};

export default DanceLabApplicationForm;
